import { Settings, WMSInfo, WMSLayerInfo } from '@ais-ui/websockets';
import { useSettings } from '../Settings/SettingsProvider';
import { useEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';

interface WMSSettings {
  wmsInfos: WMSInfo[];
  enabledWmsLayers: EnabledWMSLayer[];
}

export interface EnabledWMSLayer {
  wmsInfo: WMSInfo;
  wmsLayerInfo: WMSLayerInfo;
}

export const getWmsLayersFromSettings = (settings: Settings): WMSInfo[] => {
  const wmsLayers: WMSInfo[] =
    settings && settings.wmsSettings ? settings.wmsSettings : [];

  // This is only here for backwards compatibility
  wmsLayers.forEach((layer) => {
    if ('singletile' in layer && !('singleTile' in layer)) {
      layer.singleTile = layer.singletile;
    }
    if ('trans' in layer && !('transparency' in layer)) {
      layer.transparency = layer.trans;
    }
  });

  return settings && settings.wmsSettings ? settings.wmsSettings : [];
};

function getEnabledWMSLayers(wmsLayers: WMSInfo[]): EnabledWMSLayer[] {
  const enabledWMS = wmsLayers
    .filter((wmsLayer) => wmsLayer.enabled)
    .flatMap((wmsLayer) =>
      (wmsLayer.layers ?? [])
        .filter((layer) => layer.enabled)
        .map((layer) => ({ wmsInfo: wmsLayer, wmsLayerInfo: layer }))
    );

  return enabledWMS;
}

export const useWMSSettings = () => {
  const { settings, dataUpdatedAt } = useSettings();

  const computedSettings = useMemo(() => {
    const fetchedWmsLayers = getWmsLayersFromSettings(settings);
    const newEnabledWmsLayers = getEnabledWMSLayers(fetchedWmsLayers);
    return {
      wmsInfos: fetchedWmsLayers,
      enabledWmsLayers: newEnabledWmsLayers,
    };
    // We have to use dataUpdatedAt here to make sure that the settings are updated because React
    //  doesn't see changes
  }, [settings, dataUpdatedAt]);

  return computedSettings;
};

export const addWmsToSettings = (
  wmsLayer: WMSInfo,
  settings: Settings
): Settings => {
  const wmsLayers = getWmsLayersFromSettings(settings);

  wmsLayers.push(wmsLayer);

  const settingsToSave = {
    ...settings,
    wmsSettings: wmsLayers,
  };

  return settingsToSave;
};

export const UpdateWmsToSettings = (
  updatedWmsLayer: WMSInfo,
  settings: Settings
): Settings => {
  const wmsLayers = getWmsLayersFromSettings(settings);

  const layerIndex = wmsLayers.findIndex(
    (layer) => layer.id === updatedWmsLayer.id
  );

  if (layerIndex !== -1) {
    wmsLayers[layerIndex] = updatedWmsLayer;
  } else {
    return settings;
  }

  const settingsToSave = {
    ...settings,
    wmsSettings: wmsLayers,
  };

  return settingsToSave;
};

export const updateActiveBaseMapInSettings = (
  activeBaseMap: string,
  settings: Settings
): Settings => {
  const settingsToSave = {
    ...settings,
    'ribbonPlugin.settings': {
      ...settings['ribbonPlugin.settings'],
      active_base_map: activeBaseMap,
    },
  };

  return settingsToSave;
};

export const deleteWmsToSettings = (
  wmsLayer: WMSInfo,
  settings: Settings
): Settings => {
  const wmsLayers = getWmsLayersFromSettings(settings);

  const filteredWmsLayers = wmsLayers.filter(
    (layer) => layer.id !== wmsLayer.id
  );

  const settingsToSave = {
    ...settings,
    wmsSettings: filteredWmsLayers,
  };

  return settingsToSave;
};

export const ToggleBaseMapInSettings = (
  settings: Settings,
  wmsSettings: WMSSettings,
  wmsLayerId: string,
  isBase: boolean
): Settings => {
  const updatedWmsInfos = wmsSettings.wmsInfos.map((wmsLayer) => {
    if (wmsLayer.layers) {
      return {
        ...wmsLayer,
        layers: wmsLayer.layers.map((layer) =>
          layer.id === wmsLayerId ? { ...layer, isBase: isBase } : layer
        ),
      };
    }
    return wmsLayer;
  });

  return {
    ...settings,
    wmsSettings: updatedWmsInfos,
  };
};

export const ToggleWmsLayerInSettings = (
  wmsLayers: WMSInfo[],
  wmsLayerId: string,
  layerName: string,
  enabled: boolean,
  settings: Settings
): Settings => {
  const layerIndex = wmsLayers.findIndex((layer) => layer.id === wmsLayerId);

  if (layerIndex !== -1) {
    const wmsLayer = wmsLayers[layerIndex];
    const matchingLayerIndex = wmsLayer?.layers?.findIndex(
      (layer) => layer.name === layerName
    );
    if (matchingLayerIndex === -1) {
      return settings;
    }

    const updatedWmsLayer = {
      ...wmsLayer,
      layers: wmsLayer.layers?.map((layer, index) =>
        index === matchingLayerIndex ? { ...layer, enabled: enabled } : layer
      ),
    };
    const updatedWmsLayers = wmsLayers.map((layer, index) =>
      index === layerIndex ? updatedWmsLayer : layer
    );

    const settingsToSave = {
      ...settings,
      wmsSettings: updatedWmsLayers,
    };

    return settingsToSave;
  } else {
    return settings;
  }
};

export const isWmsLayerShownOnStartUp = (
  settings: Settings,
  wmsLayerId: string
): boolean => {
  return (
    settings?.wmsSettings?.some((wmsLayer) =>
      wmsLayer?.layers?.some(
        (layer) => layer?.id === wmsLayerId && layer?.enabled
      )
    ) ?? false
  );
};
